// Settings → Automated text messages tab.
//
// Retailers customise the WORDING of the platform's existing automated SMS
// (payment due/reminder/received, booking confirmation, quote-expiry, welcome).
// There is NO new send path and NO freeform composer — the same automated
// senders still fire server-side through sms.js. This tab only edits the copy.
//
// Each automated SMS is a fixed "slot" with locked, server-computed variables
// (amount, retailer name, portal link, …). Retailers may move those {{tokens}}
// around in the wording but can never inject a different amount or URL — the
// server only substitutes the slot's whitelisted tokens. Platform-only
// messaging is unaffected.
//
// Backend (migration 0064, RLS-scoped to the signed-in retailer):
//   GET    /api/portal/message-templates/automated
//   PUT    /api/portal/message-templates/automated/:key   { body }
//   DELETE /api/portal/message-templates/automated/:key    (reset to default)

import { iconMarkup, confirmDialog } from "@tkos/shared-ui";

import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./sms-notifications.css";

interface SlotVar {
  token: string;
  label: string;
  example: string;
}
interface Slot {
  key: string;
  label: string;
  description: string;
  defaultBody: string;
  body: string;
  isCustom: boolean;
  vars: SlotVar[];
  length: number;
  segments: number;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// Client-side mirror of the server's render: substitute ONLY the slot's
// whitelisted tokens with their example values; strip any other {{...}}.
function previewOf(slot: Slot, text: string): string {
  const allowed = new Map(slot.vars.map((v) => [v.token, v.example]));
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, token: string) =>
    allowed.has(token) ? String(allowed.get(token)) : "");
}

// GSM-7 segment hint (matches the server's segmentInfo). UI guidance only —
// the real send is handled by Twilio.
function segInfo(text: string): { length: number; segments: number } {
  const length = text.length;
  const segments = length === 0 ? 0 : length <= 160 ? 1 : Math.ceil(length / 153);
  return { length, segments };
}

const smsNotificationsTab: SettingsTab = {
  id: "sms-notifications",
  label: "Automated text messages",
  icon: iconMarkup("send", { size: "sm" }),
  mount(container: HTMLElement, ctx: PageContext) {
    const ac = new AbortController();
    let disposed = false;
    let slots: Slot[] = [];

    container.innerHTML = `
      <section class="set-smsn-root" aria-labelledby="set-smsn-h">
        <h2 id="set-smsn-h" class="set-section-title">Automated text messages</h2>
        <p class="set-help">
          Customise the wording of the text messages the platform sends your
          customers automatically — payment reminders, booking confirmations,
          quote-expiry nudges and the welcome message. The details in
          <code>{{double braces}}</code> are filled in for you (the amount, the
          date, your business name) and can't be changed — move them around the
          wording as you like. Leave a message on its default and it sends
          exactly as before.
        </p>
        <div class="set-smsn-list" data-role="list"></div>
      </section>
    `;

    const listEl = container.querySelector<HTMLElement>('[data-role="list"]')!;

    function renderList(): void {
      if (slots.length === 0) {
        listEl.innerHTML = `<div class="set-smsn-empty">Couldn't load the automated messages.</div>`;
        return;
      }
      listEl.innerHTML = slots.map((s) => {
        const chips = s.vars.map((v) =>
          `<button type="button" class="set-smsn-chip" data-token="${esc(v.token)}" title="${esc(v.label)} — e.g. ${esc(v.example)}">{{${esc(v.token)}}}</button>`,
        ).join("");
        return `
        <div class="set-smsn-card" data-key="${esc(s.key)}">
          <div class="set-smsn-card-head">
            <span class="set-smsn-card-label">${esc(s.label)}</span>
            <span class="set-smsn-badge ${s.isCustom ? "set-smsn-badge--custom" : "set-smsn-badge--default"}" data-role="badge">${s.isCustom ? "Customised" : "Default"}</span>
          </div>
          <p class="set-smsn-card-desc">${esc(s.description)}</p>
          <div class="set-smsn-chips" aria-label="Insert a detail">${chips}</div>
          <textarea class="set-input set-smsn-body" rows="3" maxlength="480" data-role="body" aria-label="${esc(s.label)} message text">${esc(s.body)}</textarea>
          <div class="set-smsn-preview" data-role="preview"></div>
          <div class="set-smsn-card-foot">
            <span class="set-smsn-count" data-role="count"></span>
            <span class="set-smsn-actions">
              <button type="button" class="set-btn set-btn-secondary set-btn-sm" data-role="reset" ${s.isCustom ? "" : "disabled"}>Reset to default</button>
              <button type="button" class="set-btn set-btn-sm" data-role="save" disabled>Save</button>
            </span>
          </div>
          <p class="set-smsn-error" data-role="error" hidden></p>
        </div>`;
      }).join("");

      // Wire each card.
      for (const card of Array.from(listEl.querySelectorAll<HTMLElement>(".set-smsn-card"))) {
        const key = card.dataset["key"]!;
        const slot = slots.find((x) => x.key === key)!;
        const bodyEl = card.querySelector<HTMLTextAreaElement>('[data-role="body"]')!;
        const previewEl = card.querySelector<HTMLElement>('[data-role="preview"]')!;
        const countEl = card.querySelector<HTMLElement>('[data-role="count"]')!;
        const saveBtn = card.querySelector<HTMLButtonElement>('[data-role="save"]')!;
        const resetBtn = card.querySelector<HTMLButtonElement>('[data-role="reset"]')!;
        const errEl = card.querySelector<HTMLElement>('[data-role="error"]')!;

        const refresh = (): void => {
          const text = bodyEl.value;
          const { length, segments } = segInfo(text);
          countEl.textContent = `${length}/480 · ${segments} SMS`;
          countEl.classList.toggle("set-smsn-count--multi", segments > 1);
          previewEl.textContent = previewOf(slot, text).trim() || "—";
          const changed = text.trim() !== slot.body.trim();
          saveBtn.disabled = !changed || text.trim().length === 0;
        };
        refresh();

        bodyEl.addEventListener("input", refresh);

        // Variable chips insert the token at the cursor.
        card.querySelectorAll<HTMLButtonElement>(".set-smsn-chip").forEach((chip) => {
          chip.addEventListener("click", () => {
            const token = `{{${chip.dataset["token"]}}}`;
            const start = bodyEl.selectionStart ?? bodyEl.value.length;
            const end = bodyEl.selectionEnd ?? bodyEl.value.length;
            bodyEl.value = bodyEl.value.slice(0, start) + token + bodyEl.value.slice(end);
            const caret = start + token.length;
            bodyEl.focus();
            bodyEl.setSelectionRange(caret, caret);
            refresh();
          });
        });

        const showErr = (m: string | null): void => { errEl.hidden = !m; errEl.textContent = m ?? ""; };

        saveBtn.addEventListener("click", () => {
          const body = bodyEl.value.trim();
          if (!body) { showErr("Message text is required."); return; }
          showErr(null);
          saveBtn.disabled = true;
          void (async () => {
            try {
              const r = await ctx.api<{ ok: boolean; body: string }>(
                `/portal/message-templates/automated/${encodeURIComponent(key)}`,
                { method: "PUT", body: JSON.stringify({ body }), signal: ac.signal },
              );
              slot.body = r.body ?? body;
              slot.isCustom = true;
              ctx.showToast(`${slot.label} message saved`, "success");
              renderList();
            } catch {
              saveBtn.disabled = false;
              showErr("Couldn't save — please try again.");
            }
          })();
        });

        resetBtn.addEventListener("click", () => {
          void (async () => {
            const ok = await confirmDialog(
              `Reset the "${slot.label}" message back to the default wording?`,
              { title: "Reset to default", confirmLabel: "Reset", destructive: true },
            );
            if (!ok || disposed) return;
            try {
              const r = await ctx.api<{ ok: boolean; body: string }>(
                `/portal/message-templates/automated/${encodeURIComponent(key)}`,
                { method: "DELETE", signal: ac.signal },
              );
              slot.body = r.body ?? slot.defaultBody;
              slot.isCustom = false;
              ctx.showToast(`${slot.label} message reset`, "success");
              renderList();
            } catch {
              ctx.showToast("Couldn't reset — please try again.", "error");
            }
          })();
        });
      }
    }

    async function load(): Promise<void> {
      try {
        const r = await ctx.api<{ ok: boolean; slots: Slot[] }>(
          "/portal/message-templates/automated", { signal: ac.signal });
        if (disposed) return;
        slots = Array.isArray(r.slots) ? r.slots : [];
        renderList();
      } catch {
        if (disposed) return;
        listEl.innerHTML = `<div class="set-smsn-empty">Couldn't load the automated messages.</div>`;
      }
    }

    void load();

    return () => { disposed = true; ac.abort(); };
  },
};

export default smsNotificationsTab;
